'use client';

import Link from 'next/link';
import { CheckCircle, Clock } from 'lucide-react';
import { DifficultyStars } from './DifficultyStars';
import { XPBadge } from './XPBadge';
import { useGameStore } from '@/store/useGameStore';
import type { Question, Category } from '@/types';
import { truncate } from '@/lib/utils';

interface Props {
  question: Question;
  category?: Category;
}

export function QuestionCard({ question, category }: Props) {
  const isAnswered = useGameStore((s) => s.isQuestionAnswered(question.id));

  const categoryColor = category?.color ?? '#5B4FE8';

  return (
    <article
      data-testid="question-card"
      data-question-id={question.id}
      data-difficulty={question.difficulty}
      data-category={question.category}
      className="card-base card-hover group relative"
      style={{ padding: '20px' }}
    >
      {/* Answered badge */}
      {isAnswered && (
        <div className="absolute top-3 right-3">
          <CheckCircle size={18} style={{ color: '#22C55E' }} aria-label="Answered" />
        </div>
      )}

      {/* Category dot */}
      <div className="flex items-center justify-between mb-3">
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
          style={{
            background: `${categoryColor}20`,
            color: categoryColor,
            border: `1px solid ${categoryColor}40`,
          }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: categoryColor }} />
          {category?.name ?? question.category}
        </span>
        <DifficultyStars difficulty={question.difficulty} size="sm" />
      </div>

      {/* Title */}
      <h3
        className="font-semibold mb-2 leading-snug"
        style={{
          fontFamily: 'var(--font-display)',
          color: 'var(--color-text-primary)',
          fontSize: '15px',
        }}
      >
        <Link
          href={`/questions/${question.slug}`}
          className="hover:text-[#5B4FE8] transition-colors after:absolute after:inset-0"
          style={{ color: 'inherit' }}
        >
          {question.title}
        </Link>
      </h3>

      {/* Short answer */}
      <p
        className="text-sm mb-4 leading-relaxed"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {truncate(question.shortAnswer, 110)}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between">
        <XPBadge xp={question.xpReward} size="sm" />
        <span
          className="flex items-center gap-1 text-xs"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <Clock size={11} />
          {question.timeToRead}m
        </span>
      </div>
    </article>
  );
}
